import React, { useEffect, useState, useRef } from 'react';
import { XIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password?: string) => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  darkMode?: boolean;
  isDeleting?: boolean;
  requirePassword?: boolean;
  passwordError?: string;
}
export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  darkMode = true,
  isDeleting = false,
  requirePassword = false,
  passwordError = ''
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  // Focus the password input when the modal opens
  useEffect(() => {
    if (isOpen && requirePassword && passwordInputRef.current) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    } else if (isOpen && modalRef.current) {
      // If no password field, focus the modal itself to capture keyboard events
      modalRef.current.focus();
    }
  }, [isOpen, requirePassword]);
  // Handle Enter key press for confirmation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only proceed if the modal is open and the key is Enter
      if (!isOpen || e.key !== 'Enter') return;
      // Check if we're not in a text input (except for our password field)
      const activeElement = document.activeElement;
      const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;
      // If we're in our password field or not in any input field, trigger confirm
      if (!isInputElement || activeElement === passwordInputRef.current) {
        if (!isDeleting && !(requirePassword && !password)) {
          e.preventDefault();
          handleConfirm();
        }
      }
    };
    // Add event listener to the window
    window.addEventListener('keydown', handleKeyDown);
    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isDeleting, requirePassword, password]);
  if (!isOpen) return null;
  const handleConfirm = () => {
    // Validate password is not empty when required
    if (requirePassword && !password.trim()) {
      // Handle empty password error
      onConfirm('');
      return;
    }
    if (requirePassword) {
      onConfirm(password);
    } else {
      onConfirm();
    }
  };
  // Stop propagation to prevent modal close when clicking inside
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1285">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20" data-id="element-1286">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-1287">
          <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-1288"></div>
        </div>
        <div ref={modalRef} onClick={handleContentClick} className={`relative inline-block w-full max-w-md rounded-lg text-left overflow-hidden shadow-xl transform transition-all ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} tabIndex={0} // Make the modal focusable
      data-id="element-1289">
          {/* Header */}
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex items-center justify-between`} data-id="element-1290">
            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-1291">
              {title}
            </h3>
            <button onClick={onClose} className={`rounded-full p-2 ${darkMode ? 'hover:bg-navy-700 text-gray-300' : 'hover:bg-gray-100 text-gray-500'} transition-colors`} data-id="element-1292">
              <XIcon className="h-5 w-5" data-id="element-1293" />
            </button>
          </div>
          {/* Content */}
          <div className={`p-6 ${darkMode ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1294">
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'} data-id="element-1295">
              {message}
            </p>
            {/* Password Field */}
            {requirePassword && <div className="mt-4" data-id="element-1296">
                <label htmlFor="delete-confirmation-password" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-1297">
                  Enter your account password
                </label>
                <div className="relative" data-id="element-1298">
                  <input id="delete-confirmation-password" ref={passwordInputRef} type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={`w-full px-3 py-2 pr-10 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} ${passwordError ? 'border-red-500' : ''}`} placeholder="Your password" data-id="element-1299" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-1300">
                    {showPassword ? <EyeOffIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1301" /> : <EyeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1302" />}
                  </button>
                </div>
                {passwordError && <p className="mt-1 text-sm text-red-500" data-id="element-1303">{passwordError}</p>}
              </div>}
          </div>
          {/* Footer */}
          <div className={`px-6 py-4 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex justify-end space-x-3`} data-id="element-1304">
            <button type="button" onClick={onClose} disabled={isDeleting} className={`px-4 py-2 rounded-md text-sm font-medium ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors ${isDeleting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1305">
              {cancelText}
            </button>
            <button type="button" onClick={handleConfirm} disabled={isDeleting || requirePassword && !password} className={`px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors ${isDeleting || requirePassword && !password ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1306">
              {isDeleting ? <span className="flex items-center" data-id="element-1307">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1308">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1309"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1310"></path>
                  </svg>
                  Deleting...
                </span> : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>;
};